import React, {useEffect, useMemo, useRef, useState} from 'react';
import axios from "axios";
import {toast} from 'react-toastify';
// @ts-ignore
import {
	Plus,
	Trash,
	PencilSimple,
	Users,
	DownloadSimple,
	Check,
	CaretLeft,
	CaretRight,
	CircleNotch,
	MagnifyingGlass,
} from '@phosphor-icons/react';
import {getBaseUrlNodeServer} from "../../config";

const HIDDEN_KEYS = new Set(['_id', 'verifiedemail']);
const PAGE_SIZES = [10, 25, 50, 100];
const FILTER_OPTIONS = [
	{id: 'none', label: 'All'},
	{id: 'has-email', label: 'Has email'},
	{id: 'has-phone', label: 'Has phone'},
	{id: 'recent', label: 'Recent'},
];

// Stable internal id for rows that don't have a Mongo _id yet (newly added).
let tempSeq = 0;
const nextTempId = () => `__tmp_${++tempSeq}_${Date.now()}`;

// Derive the visible column order from the first row (skip _id, verifiedemail).
function deriveHeader(rows) {
	for (const r of rows) {
		const keys = Object.keys(r || {}).filter(k => !HIDDEN_KEYS.has(k));
		if (keys.length) return keys;
	}
	return [];
}

function rowMatchesSearch(row, header, q) {
	if (!q) return true;
	const needle = q.toLowerCase();
	for (const k of header) {
		const v = row[k];
		if (v != null && String(v).toLowerCase().includes(needle)) return true;
	}
	return false;
}

function rowHasEmail(row, header) {
	const emailKey = header.find(k => /email|e-mail/i.test(k));
	if (!emailKey) return false;
	const v = row[emailKey];
	return v != null && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
}

function rowHasPhone(row, header) {
	const phoneKey = header.find(k => /phone|mobile|cell|tel/i.test(k));
	if (!phoneKey) return false;
	const v = row[phoneKey];
	if (v == null) return false;
	const digits = String(v).replace(/\D/g, '');
	return digits.length >= 7;
}

// Mongo ObjectId timestamp (first 4 bytes = unix seconds). Use for "recent" filter.
function objectIdToMs(id) {
	if (typeof id !== 'string' || id.length !== 24) return 0;
	const ts = parseInt(id.substring(0, 8), 16);
	return Number.isFinite(ts) ? ts * 1000 : 0;
}

const SkeletonRows = ({cols}) => (
	<>
		{Array.from({length: 6}).map((_, i) => (
			<tr key={i} className="animate-pulse">
				{Array.from({length: cols}).map((__, j) => (
					<td key={j} className="px-4 py-3">
						<div className="h-3 bg-slate-100 rounded w-3/4"/>
					</td>
				))}
				<td className="px-4 py-3 w-10">
					<div className="h-3 bg-slate-100 rounded w-4 ml-auto"/>
				</td>
			</tr>
		))}
	</>
);

const EmptyState = ({onAdd, onUpload}) => (
	<div className="rounded-2xl border border-slate-200 bg-white shadow-sm py-16 px-6 text-center">
		<div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
			<Users size={32} weight="duotone" color="#10B981"/>
		</div>
		<h3 className="text-xl font-semibold text-slate-900 mb-2">No contacts yet</h3>
		<p className="text-base text-slate-600 max-w-md mx-auto mb-6">
			Upload a CSV from your existing tools, or add your first contact by hand to start sending campaigns.
		</p>
		<div className="flex items-center justify-center gap-3">
			<button
				type="button"
				onClick={onUpload}
				className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-5 py-2.5 font-medium">
				Upload a CSV
			</button>
			<button
				type="button"
				onClick={onAdd}
				className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5 py-2.5 font-semibold shadow-sm">
				<Plus size={16} weight="bold"/>
				Add manually
			</button>
		</div>
	</div>
);

const EditableTable = ({
	initialData,
	isLoading,
	addRowSignal = 0,
	onAddContact,
	onSwitchToUpload,
	refetchContacts,
}) => {
	const verifiedemail = sessionStorage.getItem("verifiedemail");

	// Local rows mirror the server data with stable client ids (`_cid`) layered in.
	// We never strip _id / verifiedemail — they go back to the server on save/delete.
	const [rows, setRows] = useState([]);
	const [perPage, setPerPage] = useState(25);
	const [page, setPage] = useState(1);
	const [sortKey, setSortKey] = useState(null);
	const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
	const [search, setSearch] = useState('');
	const [filter, setFilter] = useState('none');
	const [editing, setEditing] = useState(null); // {cid, key, value}
	const [savingCid, setSavingCid] = useState(null);
	const [removingCid, setRemovingCid] = useState(null);
	const initialSyncDone = useRef(false);
	const editingRef = useRef(null); // mirrors `editing`; commit nulls it so stale blur after Enter is a no-op
	const rowsRef = useRef(rows); // synchronous mirror so commitEdit can read the latest row state without a stale closure

	const updateEditing = (next) => {
		editingRef.current = next;
		setEditing(next);
	};

	// Mirror rows into rowsRef every render so commitEdit + deleteRow can
	// read the latest row state synchronously without stale closures.
	rowsRef.current = rows;

	// Sync incoming server data → local rows, attaching a stable client id.
	// Preserves _cid for rows the server already knows (matched by _id) AND
	// preserves any locally-added blank rows the user hasn't saved yet (no _id).
	useEffect(() => {
		if (!Array.isArray(initialData)) return;
		setRows(prev => {
			const prevByMongoId = new Map();
			const unsavedLocal = [];
			prev.forEach(r => {
				if (r._id) {
					prevByMongoId.set(r._id, r._cid);
				} else {
					unsavedLocal.push(r);
				}
			});
			const fromServer = initialData.map(r => ({
				...r,
				_cid: (r._id && prevByMongoId.get(r._id)) || nextTempId(),
			}));
			// Keep unsaved local rows at the top so the user doesn't lose them
			// when a refetch arrives mid-edit.
			return [...unsavedLocal, ...fromServer];
		});
		initialSyncDone.current = true;
	}, [initialData]);

	// "+ Add contact" from page header — append blank row + reset paging to page 1.
	useEffect(() => {
		if (addRowSignal > 0) addBlankRow();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addRowSignal]);

	const header = useMemo(() => {
		const h = deriveHeader(rows);
		// Fallback header if backend returned nothing yet but user wants to add manually.
		if (h.length === 0) return ['First Name', 'Last Name', 'Email'];
		return h;
	}, [rows]);

	// Filter pipeline: filter pills → search → sort.
	const filteredRows = useMemo(() => {
		let out = rows;
		if (filter === 'has-email') out = out.filter(r => rowHasEmail(r, header));
		else if (filter === 'has-phone') out = out.filter(r => rowHasPhone(r, header));
		else if (filter === 'recent') {
			out = [...out].sort((a, b) => objectIdToMs(b._id) - objectIdToMs(a._id));
		}
		if (search && search.trim()) {
			out = out.filter(r => rowMatchesSearch(r, header, search.trim()));
		}
		if (sortKey && filter !== 'recent') {
			out = [...out].sort((a, b) => {
				const av = (a[sortKey] == null ? '' : String(a[sortKey])).toLowerCase();
				const bv = (b[sortKey] == null ? '' : String(b[sortKey])).toLowerCase();
				if (av < bv) return sortDir === 'asc' ? -1 : 1;
				if (av > bv) return sortDir === 'asc' ? 1 : -1;
				return 0;
			});
		}
		return out;
	}, [rows, header, filter, search, sortKey, sortDir]);

	const totalCount = filteredRows.length;
	const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
	const safePage = Math.min(page, totalPages);
	const startIdx = (safePage - 1) * perPage;
	const pageRows = filteredRows.slice(startIdx, startIdx + perPage);

	useEffect(() => {
		// Snap back to page 1 whenever filter/search changes the underlying set.
		setPage(1);
	}, [search, filter, perPage]);

	function addBlankRow() {
		const blank = {_cid: nextTempId(), verifiedemail};
		header.forEach(k => {
			blank[k] = '';
		});
		setRows(prev => [blank, ...prev]);
		setPage(1);
		// Open the first cell of the new row for immediate typing.
		setTimeout(() => {
			updateEditing({cid: blank._cid, key: header[0] || 'First Name', value: ''});
		}, 0);
	}

	function cycleSort(key) {
		if (sortKey !== key) {
			setSortKey(key);
			setSortDir('asc');
			return;
		}
		if (sortDir === 'asc') {
			setSortDir('desc');
			return;
		}
		// asc → desc → none
		setSortKey(null);
		setSortDir('asc');
	}

	function startEdit(row, key) {
		updateEditing({cid: row._cid, key, value: row[key] == null ? '' : String(row[key])});
	}

	function cancelEdit() {
		updateEditing(null);
	}

	async function commitEdit() {
		// Read latest editing via ref so stale blur after Enter is a no-op.
		const current = editingRef.current;
		if (!current) return;
		editingRef.current = null;
		const {cid, key, value} = current;
		// Read latest row state from rowsRef so a refetch arriving between
		// editStart and commit doesn't leave us reading a stale closure.
		const target = rowsRef.current.find(r => r._cid === cid);
		if (!target) {
			setEditing(null);
			return;
		}
		const updated = {...target, [key]: value};
		setRows(prev => prev.map(r => (r._cid === cid ? updated : r)));
		setEditing(null); // ref already nulled at top

		// Build payload: backend's createDictionary indexes `data` numerically,
		// so it MUST be a positional array aligned with `header`. Sending an
		// object silently no-ops the upsert (and previously corrupted writes).
		const dataArray = header.map(k => (updated[k] == null ? '' : updated[k]));
		setSavingCid(cid);
		try {
			axios.defaults.baseURL = getBaseUrlNodeServer();
			await axios.post('saveusercontact', {
				data: dataArray,
				header,
				verifiedemail,
			});
			toast.success('Saved', {autoClose: 1200});
			// Refetch in the background so newly-inserted rows pick up their _id.
			if (typeof refetchContacts === 'function') refetchContacts();
		} catch (err) {
			console.error('saveusercontact failed', err);
			toast.error("Couldn't save contact");
		} finally {
			setSavingCid(null);
		}
	}

	async function deleteRow(row) {
		// eslint-disable-next-line no-alert
		if (!window.confirm('Delete this contact? This cannot be undone.')) return;
		setRemovingCid(row._cid);
		// Soft-fade then remove locally after ~280ms.
		setTimeout(() => {
			setRows(prev => prev.filter(r => r._cid !== row._cid));
			setRemovingCid(null);
		}, 280);
		// If the row was never saved (no _id), there's nothing to delete server-side.
		if (!row._id) return;
		// Backend matches the row to delete by Email; createDictionary indexes
		// `data` numerically, so we must send a positional array aligned with
		// `header` (same shape as save). Object form would match {verifiedemail}
		// alone and could delete the wrong contact.
		const dataArray = header.map(k => (row[k] == null ? '' : row[k]));
		try {
			axios.defaults.baseURL = getBaseUrlNodeServer();
			await axios.post('deleteusercontact', {
				data: dataArray,
				header,
				verifiedemail,
			});
			toast.info('Contact removed', {autoClose: 1500});
		} catch (err) {
			console.error('deleteusercontact failed', err);
			toast.error("Couldn't delete contact");
		}
	}

	function downloadCsv() {
		const escape = (v) => {
			const s = v == null ? '' : String(v);
			if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
			return s;
		};
		const lines = [header.map(escape).join(',')];
		rows.forEach(r => {
			lines.push(header.map(k => escape(r[k])).join(','));
		});
		const blob = new Blob([lines.join('\r\n')], {type: 'text/csv'});
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = 'neugence_contacts.csv';
		a.style.display = 'none';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}

	// Loading skeleton: render the card shell with skeleton rows inside.
	if (isLoading) {
		return (
			<div className="rounded-2xl shadow-sm border border-slate-200 bg-white overflow-hidden">
				<div className="px-4 py-3 border-b border-slate-100 bg-white flex items-center justify-between">
					<div className="h-8 w-56 bg-slate-100 rounded-lg animate-pulse"/>
					<div className="h-8 w-32 bg-slate-100 rounded-lg animate-pulse"/>
				</div>
				<table className="w-full">
					<tbody className="divide-y divide-slate-100">
						<SkeletonRows cols={4}/>
					</tbody>
				</table>
			</div>
		);
	}

	if (rows.length === 0) {
		return <EmptyState onAdd={onAddContact || addBlankRow} onUpload={onSwitchToUpload}/>;
	}

	return (
		<div className="rounded-2xl shadow-sm border border-slate-200 bg-white overflow-hidden">
			{/* Toolbar */}
			<div className="bg-white border-b border-slate-100 px-4 py-3 flex flex-wrap items-center gap-3">
				<div className="relative">
					<MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
					<input
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search contacts…"
						className="border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm w-56 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
					/>
				</div>

				<select
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
					className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none">
					{FILTER_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
				</select>

				<div className="text-sm text-slate-500">
					<span className="font-semibold text-slate-900">{totalCount}</span>
					{totalCount === 1 ? ' contact' : ' contacts'}
				</div>

				<div className="ml-auto flex flex-wrap items-center gap-2">
					<select
						value={perPage}
						onChange={(e) => setPerPage(Number(e.target.value))}
						className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none">
						{PAGE_SIZES.map(n => <option key={n} value={n}>{n} / page</option>)}
					</select>

					<button
						type="button"
						onClick={downloadCsv}
						title="Download CSV"
						className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium">
						<DownloadSimple size={14} weight="bold"/>
						Export
					</button>

					<button
						type="button"
						onClick={addBlankRow}
						className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-3 py-2 text-sm font-medium">
						<Plus size={14} weight="bold"/>
						Add row
					</button>
				</div>
			</div>

			{/* Table */}
			<div className="overflow-x-auto">
				<table className="w-full">
					<thead>
						<tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide font-semibold">
							{header.map(key => {
								const isSorted = sortKey === key;
								return (
									<th
										key={key}
										onClick={() => cycleSort(key)}
										className="px-4 py-3 text-left cursor-pointer select-none hover:text-slate-900">
										<span className="inline-flex items-center gap-1">
											{key}
											{isSorted && (
												<span className="text-emerald-600">
													{sortDir === 'asc' ? '▲' : '▼'}
												</span>
											)}
										</span>
									</th>
								);
							})}
							<th className="px-4 py-3 text-right w-12">{/* actions */}</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-slate-100">
						{pageRows.length === 0 ? (
							<tr>
								<td colSpan={header.length + 1} className="px-4 py-12 text-center text-sm text-slate-500">
									No contacts match your search.
								</td>
							</tr>
						) : (
							pageRows.map(row => {
								const fading = removingCid === row._cid;
								const saving = savingCid === row._cid;
								return (
									<tr
										key={row._cid}
										className={
											'group transition-opacity duration-200 hover:bg-emerald-50/40 ' +
											(fading ? 'opacity-0' : 'opacity-100')
										}>
										{header.map(key => {
											const isEditing = editing && editing.cid === row._cid && editing.key === key;
											const cellValue = row[key] == null ? '' : String(row[key]);
											return (
												<td
													key={key}
													onClick={() => !isEditing && startEdit(row, key)}
													className="px-4 py-3 text-sm text-slate-900 align-middle cursor-text">
													{isEditing ? (
														<input
															autoFocus
															type="text"
															value={editing.value}
															onChange={(e) => updateEditing({...editing, value: e.target.value})}
															onKeyDown={(e) => {
																if (e.key === 'Enter') {
																	e.preventDefault();
																	commitEdit();
																} else if (e.key === 'Escape') {
																	e.preventDefault();
																	cancelEdit();
																}
															}}
															onBlur={commitEdit}
															className="w-full border border-emerald-300 rounded-lg px-2 py-1 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
														/>
													) : (
														<span className="inline-flex items-center gap-1.5">
															{cellValue || <span className="text-slate-300">—</span>}
															{saving && key === header[0] && (
																<CircleNotch size={12} className="animate-spin text-emerald-500"/>
															)}
															<PencilSimple
																size={12}
																className="opacity-0 group-hover:opacity-50 text-slate-400 transition-opacity"/>
														</span>
													)}
												</td>
											);
										})}
										<td className="px-4 py-3 text-right">
											{saving ? (
												<Check size={16} className="inline text-emerald-500"/>
											) : (
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														deleteRow(row);
													}}
													title="Delete contact"
													className="inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
													<Trash size={16} weight="bold"/>
												</button>
											)}
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			{/* Pagination footer */}
			<div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-white">
				<div className="text-sm text-slate-600">
					{totalCount === 0 ? 'Showing 0' : `Showing ${startIdx + 1}–${Math.min(startIdx + perPage, totalCount)} of ${totalCount}`}
				</div>
				<Pagination page={safePage} totalPages={totalPages} onChange={setPage}/>
			</div>
		</div>
	);
};

const Pagination = ({page, totalPages, onChange}) => {
	const buttons = useMemo(() => {
		const out = [];
		const max = totalPages;
		if (max <= 7) {
			for (let i = 1; i <= max; i++) out.push(i);
			return out;
		}
		out.push(1);
		const start = Math.max(2, page - 1);
		const end = Math.min(max - 1, page + 1);
		if (start > 2) out.push('…');
		for (let i = start; i <= end; i++) out.push(i);
		if (end < max - 1) out.push('…');
		out.push(max);
		return out;
	}, [page, totalPages]);

	const baseBtn = 'inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors';
	return (
		<div className="flex items-center gap-1">
			<button
				type="button"
				onClick={() => onChange(Math.max(1, page - 1))}
				disabled={page <= 1}
				className={baseBtn + ' border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent'}>
				<CaretLeft size={14} weight="bold"/>
			</button>
			{buttons.map((b, i) => (
				typeof b === 'number' ? (
					<button
						key={`${b}-${i}`}
						type="button"
						onClick={() => onChange(b)}
						className={
							baseBtn + ' ' +
							(b === page
								? 'bg-emerald-500 text-white shadow-sm'
								: 'text-slate-600 hover:bg-slate-50')
						}>
						{b}
					</button>
				) : (
					<span key={`e-${i}`} className="text-slate-400 px-1 text-sm">…</span>
				)
			))}
			<button
				type="button"
				onClick={() => onChange(Math.min(totalPages, page + 1))}
				disabled={page >= totalPages}
				className={baseBtn + ' border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent'}>
				<CaretRight size={14} weight="bold"/>
			</button>
		</div>
	);
};

export default EditableTable;
